"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { classNames } from "@/lib/utils";

/**
 * FlickeringGrid - Animated grid background with subtle flickering squares
 *
 * Minimal implementation extracted from Magic UI, optimized for Outfame.
 * Uses Canvas API for hardware-accelerated rendering.
 *
 * Features:
 * - IntersectionObserver pauses animation when off-screen
 * - ResizeObserver handles responsive sizing
 * - Frame throttling (50ms) for performance
 * - High-DPI display support
 */

interface FlickeringGridProps {
  /** Size of each square in pixels */
  squareSize?: number;
  /** Gap between squares in pixels */
  gridGap?: number;
  /** Probability of flickering per frame (0-1) */
  flickerChance?: number;
  /** Color of squares (hex) */
  color?: string;
  /** Maximum opacity for squares (0-1) */
  maxOpacity?: number;
  /** Use rounded squares */
  squareRound?: number;
  className?: string;
}

// Parse hex color to RGB array
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
}
export function FlickeringGrid(t0) {
  const $ = _c(25);
  const {
    squareSize: t1,
    gridGap: t2,
    flickerChance: t3,
    color: t4,
    maxOpacity: t5,
    squareRound: t6,
    className
  } = t0;
  const squareSize = t1 === undefined ? 3 : t1;
  const gridGap = t2 === undefined ? 24 : t2;
  const flickerChance = t3 === undefined ? 0.08 : t3;
  const color = t4 === undefined ? "#141414" : t4;
  const maxOpacity = t5 === undefined ? 0.15 : t5;
  const squareRound = t6 === undefined ? 1 : t6;
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(0);
  const gridParamsRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const lastRenderTimeRef = useRef(0);
  let t7;
  if ($[0] !== color) {
    t7 = hexToRgb(color);
    $[0] = color;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const rgb = t7;
  let t8;
  if ($[2] !== gridGap || $[3] !== maxOpacity || $[4] !== squareSize) {
    t8 = (canvas, width, height) => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const cols = Math.ceil(width / (squareSize + gridGap));
      const rows = Math.ceil(height / (squareSize + gridGap));
      if (gridParamsRef.current && gridParamsRef.current.cols === cols && gridParamsRef.current.rows === rows) {
        return {
          ...gridParamsRef.current,
          dpr
        };
      }
      const squares = new Float32Array(cols * rows);
      for (let i = 0; i < squares.length; i++) {
        squares[i] = Math.random() * maxOpacity;
      }
      return {
        cols,
        rows,
        squares,
        dpr
      };
    };
    $[2] = gridGap;
    $[3] = maxOpacity;
    $[4] = squareSize;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const setupCanvas = t8;
  let t9;
  if ($[6] !== flickerChance || $[7] !== maxOpacity) {
    t9 = (squares_0, deltaTime) => {
      if (flickerChance <= 0) {
        return;
      }
      for (let i_0 = 0; i_0 < squares_0.length; i_0++) {
        if (Math.random() < flickerChance * deltaTime) {
          squares_0[i_0] = Math.random() * maxOpacity;
        }
      }
    };
    $[6] = flickerChance;
    $[7] = maxOpacity;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const updateSquares = t9;
  let t10;
  if ($[9] !== gridGap || $[10] !== rgb || $[11] !== squareRound || $[12] !== squareSize) {
    t10 = (ctx, width_0, height_0, cols_0, rows_0, squares_1, dpr_0) => {
      ctx.clearRect(0, 0, width_0, height_0);
      const scaledSize = squareSize * dpr_0;
      const scaledStep = (squareSize + gridGap) * dpr_0;
      const opacityMap = new Map();
      for (let i_1 = 0; i_1 < cols_0; i_1++) {
        for (let j = 0; j < rows_0; j++) {
          const opacity = squares_1[i_1 * rows_0 + j];
          const roundedOpacity = Math.round(opacity * 50) / 50;
          if (!opacityMap.has(roundedOpacity)) {
            opacityMap.set(roundedOpacity, []);
          }
          opacityMap.get(roundedOpacity).push({
            x: i_1 * scaledStep,
            y: j * scaledStep
          });
        }
      }
      opacityMap.forEach((positions, opacity_0) => {
        ctx.fillStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity_0})`;
        positions.forEach(t11 => {
          const {
            x,
            y
          } = t11;
          if (squareRound > 0) {
            ctx.beginPath();
            ctx.roundRect(x, y, scaledSize, scaledSize, squareRound * dpr_0);
            ctx.fill();
          } else {
            ctx.fillRect(x, y, scaledSize, scaledSize);
          }
        });
      });
    };
    $[9] = gridGap;
    $[10] = rgb;
    $[11] = squareRound;
    $[12] = squareSize;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const drawGrid = t10;
  let t11;
  let t12;
  if ($[14] !== drawGrid || $[15] !== isInView || $[16] !== setupCanvas || $[17] !== updateSquares) {
    t11 = () => {
      const canvas_0 = canvasRef.current;
      const container = containerRef.current;
      if (!canvas_0 || !container) {
        return;
      }
      const ctx_0 = canvas_0.getContext("2d", {
        alpha: true
      });
      if (!ctx_0) {
        return;
      }
      const updateCanvasSize = () => {
        const newWidth = container.clientWidth;
        const newHeight = container.clientHeight;
        gridParamsRef.current = setupCanvas(canvas_0, newWidth, newHeight);
      };
      updateCanvasSize();
      let lastTime = 0;
      const animate = time => {
        if (!isInView) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }
        if (time - lastRenderTimeRef.current < 50) {
          animationRef.current = requestAnimationFrame(animate);
          return;
        }
        if (!gridParamsRef.current) {
          updateCanvasSize();
          animationRef.current = requestAnimationFrame(animate);
          return;
        }
        lastRenderTimeRef.current = time;
        const deltaTime_0 = (time - lastTime) / 1000;
        lastTime = time;
        updateSquares(gridParamsRef.current.squares, deltaTime_0);
        drawGrid(ctx_0, canvas_0.width, canvas_0.height, gridParamsRef.current.cols, gridParamsRef.current.rows, gridParamsRef.current.squares, gridParamsRef.current.dpr);
        animationRef.current = requestAnimationFrame(animate);
      };
      const resizeObserver = new ResizeObserver(updateCanvasSize);
      resizeObserver.observe(container);
      const intersectionObserver = new IntersectionObserver(t13 => {
        const [entry] = t13;
        return setIsInView(entry.isIntersecting);
      }, {
        threshold: 0.1
      });
      intersectionObserver.observe(canvas_0);
      if (isInView) {
        animationRef.current = requestAnimationFrame(animate);
      }
      return () => {
        cancelAnimationFrame(animationRef.current);
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
      };
    };
    t12 = [setupCanvas, updateSquares, drawGrid, isInView];
    $[14] = drawGrid;
    $[15] = isInView;
    $[16] = setupCanvas;
    $[17] = updateSquares;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  if ($[20] !== className) {
    t13 = classNames("h-full w-full", className);
    $[20] = className;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <canvas ref={canvasRef} className="pointer-events-none" />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t13) {
    t15 = <div ref={containerRef} className={t13}>{t14}</div>;
    $[23] = t13;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
}